import * as Sentry from '@sentry/vue';
import { authApi, publicApi } from '../utils/api';

const story_board = {
  namespaced: true,
  state: {
    editor_sb_toc: [],
    viewer_sb_toc: [],
    toc_loaded: false,
    editor_selected_lv1: null,
    editor_selected_lv2: null,
    viewer_selected_lv1: null,
    viewer_selected_lv2: null,
    disclosures: [], // 2레벨의 하위 컨텐츠들
    viewer_disclosure_map: {}, // 2레벨의 하위 컨텐츠들
    viewer_disclosure_map_loaded: false,
    compare_disclosures: [], // disclosures와 비교 및 덮어쓰기를 위한 데이터 (추가)
    sb_log_id: null,
    translate_disclosures: [], // 1레벨밑의 모든 하위컨텐츠들
    disclosures_loaded: false,
    is_fetching_lv2_disclosure: false,
    selectedLv1PrintOption: [],
    cursor_num: 0,
    main_setting: null,
    page_accessibility: true,
    current_ban_member: [],
    editing_presences: {},
    print_mode: false,
    pending_open_comment_disclosure_id: null,
    // 메시지함에서 시작된 댓글 이동에서 대상 disclosure/lv 키 검증을 위해
    // 어떤 mention(notice)으로부터 이동을 트리거했는지 함께 기록 (issue #769)
    pending_open_comment_mention_id: null,
    // 다른 보고서로 이동 시 update_report_code를 먼저 호출하고 router.push를 그 후에
    // 호출하기 위해, lv1/lv2 키를 store에 임시 저장한다. selectLvFromRouteOrFirst가
    // $route.query보다 이 값을 우선 사용하여 첫 lv로 튀는 깜빡임(#745)을 방지하면서도
    // selected_report/is_able_to_edit이 새 보고서 기준으로 즉시 계산되도록 한다 (issue #769).
    pending_lv_keys: null,
    sampleData: [
      {
        key: 'sustainability',
        background_color: null,
        name_ea: 'sustainability',
        name: '지속가능성',
        cover_path: 'sample/sb_cover_images/sample_sustainability.jpg',
        children: [
          { key: 'sustainability1', name: 'CEO 메시지', name_ea: 'CEO Message' },
          { key: 'sustainability2', name: '사업소개', name_ea: 'Business Introduction' },
          { key: 'sustainability3', name: '중요성평가', name_ea: 'Importance Assessment' }
        ]
      },
      {
        key: 'environment',
        background_color: null,
        name_ea: 'environment',
        name: '환경',
        cover_path: 'sample/sb_cover_images/sample_environment.jpg',
        children: [
          { key: 'environment1', name: '기후변화 대응', name_ea: 'Climate Change Response' },
          { key: 'environment2', name: '친환경 솔루션', name_ea: 'Eco-friendly Solutions' },
          {
            key: 'environment3',
            name: '환경경영 전략',
            name_ea: 'Environmental Management Strategy'
          }
        ]
      },
      {
        key: 'social',
        background_color: null,
        name_ea: 'social',
        name: '사회',
        cover_path: 'sample/sb_cover_images/sample_social.jpg',
        children: [
          { key: 'social1', name: '안전보건', name_ea: 'Safety and Health' },
          { key: 'social2', name: '인권', name_ea: 'Human Rights' },
          { key: 'social3', name: '구성원', name_ea: 'Members' },
          { key: 'social4', name: '협력사', name_ea: 'Partners' },
          { key: 'social5', name: '지역사회', name_ea: 'Local Community' }
        ]
      },
      {
        key: 'governance',
        background_color: null,
        name_ea: 'governance',
        name: '거버넌스',
        cover_path: 'sample/sb_cover_images/sample_governance.jpg',
        children: [
          { key: 'governance1', name: '지배구조', name_ea: 'Governance' },
          { key: 'governance2', name: '리스크 관리', name_ea: 'Risk Management' },
          { key: 'governance3', name: '윤리경영', name_ea: 'Ethical Management' },
          { key: 'governance5', name: '정보보호', name_ea: 'Information Security' }
        ]
      },

      // appendix의 경우 백엔드에서 별도 처리가 들어감
      {
        key: 'appendix',
        background_color: null,
        name_ea: 'appendix',
        name: '부록',
        cover_path: 'sample/sb_cover_images/sample_appendix.jpg',
        is_appendix: true,
        children: [
          { key: 'appendix_gri', name: 'GRI Index', name_ea: 'GRI Index', appendix_type: 'GRI' },
          {
            key: 'appendix_sasb',
            name: 'SASB Index',
            name_ea: 'SASB Index',
            appendix_type: 'SASB'
          }, // 보고서의 선택 프레임워크에 따라 SASB, TCFD선택 유무가 달라져야 함
          {
            key: 'appendix_tcfd',
            name: 'TCFD Index',
            name_ea: 'TCFD Index',
            appendix_type: 'TCFD'
          }, // 보고서의 선택 프레임워크에 따라 SASB, TCFD선택 유무가 달라져야 함
          {
            key: 'appendix_data_center',
            name: 'Data Center',
            name_ea: 'Data Index',
            appendix_type: 'DATACENTER'
          },
          {
            key: 'appendix_assurance',
            name: '제 3자 검증',
            name_ea: 'Third-Party Verification',
            appendix_type: 'ASSURANCE'
          }
        ]
      }
    ]
  },

  mutations: {
    update_main_setting(state, main_setting) {
      state.main_setting = JSON.parse(JSON.stringify(main_setting));
    },
    update_specific_main_setting(state, setting) {
      state.main_setting = Object.assign({}, state.main_setting, setting);
    },
    update_cursor_num(state, num) {
      state.cursor_num = num;
    },
    update_toc_loaded(state, flag) {
      state.toc_loaded = flag;
    },
    update_viewer_disclosure_map_loaded(state, flag) {
      state.viewer_disclosure_map_loaded = flag;
    },
    update_print_option(state, selected_list) {
      state.selectedLv1PrintOption = selected_list;
    },
    update_current_ban_member(state, ban_list) {
      state.current_ban_member = ban_list;
    },
    set_pending_open_comment_disclosure_id(state, disclosure_id) {
      state.pending_open_comment_disclosure_id = disclosure_id;
    },
    set_pending_open_comment_mention_id(state, mention_id) {
      state.pending_open_comment_mention_id = mention_id;
    },
    set_pending_lv_keys(state, payload) {
      // payload: { lv1, lv2 } 또는 null
      state.pending_lv_keys = payload;
    },
    set_editing_presences(state, presences) {
      state.editing_presences = { ...presences };
    },
    update_viewer_disclosure_map(state, viewer_disclosure_map) {
      const sortByOrderIndex = disclosures => {
        if (!Array.isArray(disclosures)) return disclosures;
        return [...disclosures].sort((a, b) => {
          const aOrder = a.order_index ?? Number.MAX_VALUE;
          const bOrder = b.order_index ?? Number.MAX_VALUE;
          if (aOrder === bOrder) return (a.id || 0) - (b.id || 0);
          return aOrder - bOrder;
        });
      };

      const sorted = {};
      for (const lv1Key in viewer_disclosure_map) {
        sorted[lv1Key] = {};
        for (const lv2Key in viewer_disclosure_map[lv1Key]) {
          sorted[lv1Key][lv2Key] = sortByOrderIndex(viewer_disclosure_map[lv1Key][lv2Key]);
        }
      }

      const merged = { ...state.viewer_disclosure_map };
      for (const lv1Key in sorted) {
        merged[lv1Key] = {
          ...(merged[lv1Key] || {}),
          ...sorted[lv1Key]
        };
      }
      state.viewer_disclosure_map = JSON.parse(JSON.stringify(merged));
    },
    prune_viewer_disclosure_map(state, report_code) {
      state.viewer_disclosure_map = Object.fromEntries(
        Object.entries(state.viewer_disclosure_map).filter(([key, _]) => key.includes(report_code))
      );
    },

    update_sb_toc(state, new_toc_list) {
      state.editor_sb_toc = new_toc_list;
    },
    update_viewer_sb_toc(state, new_toc_list) {
      state.viewer_sb_toc = new_toc_list;
    },
    update_one_sb_toc(state, content) {
      state.editor_sb_toc = [
        ...state.editor_sb_toc.map(item => (item.key === content.key ? content : item))
      ];
    },
    update_selected_lv1(state, payload) {
      if (payload?.view_mode === 'viewer') {
        state.viewer_selected_lv1 = payload?.lv1_info;
      } else {
        state.editor_selected_lv1 = payload?.lv1_info;
      }
    },

    update_selected_lv2(state, payload) {
      if (payload?.view_mode === 'viewer') {
        state.viewer_selected_lv2 = payload?.lv2_info;
      } else {
        state.editor_selected_lv2 = payload?.lv2_info;

        if (payload?.lv2_info?.lv1_key) {
          state.editor_sb_toc = [
            ...state.editor_sb_toc.map(item =>
              item.key === payload?.lv2_info?.lv1_key
                ? {
                    ...item,
                    children: item.children.map(child =>
                      child.key === payload?.lv2_info.key ? payload?.lv2_info : child
                    )
                  }
                : item
            )
          ];
        }
      }
    },
    reorder_selected_lv2_title_content(state, title_disclosure) {
      state.editor_selected_lv2.children = title_disclosure;
    },
    remove_selected_lv2_title_content(state, disclosure_id) {
      if (!state.editor_selected_lv2 || !Array.isArray(state.editor_selected_lv2.children)) {
        return;
      }
      state.editor_selected_lv2.children = state.editor_selected_lv2.children.filter(
        item => item.id !== disclosure_id
      );
    },
    update_is_fetching_lv2_disclosure(state, flag) {
      state.is_fetching_lv2_disclosure = flag;
    },
    update_disclosure(state, disclosure) {
      state.disclosures = [
        ...(state.disclosures || []).map(item =>
          item.id === disclosure.id
            ? {
                ...item,
                ...disclosure
              }
            : item
        )
      ];
    },
    // ERT 콘텐츠(data_table, chart, business_case, document) 내용 변경 시
    // disclosures 내 해당 항목의 data를 동기화
    update_disclosure_ert_data(state, { content_id, type, data }) {
      state.disclosures = (state.disclosures || []).map(item => {
        if (item.type === type && item.data?.id === content_id) {
          return { ...item, data: { ...item.data, ...data } };
        }
        return item;
      });
    },
    update_compare_disclosures(state, payload) {
      // TODO: 백엔드 또는 다른 로직에서 전달받은 데이터를 깊은 복사하여 compare_disclosures를 업데이트합니다.
      state.compare_disclosures = JSON.parse(JSON.stringify(payload.compareData));
      state.sb_log_id = JSON.parse(JSON.stringify(payload.sb_log_id));
    },
    update_translate_disclosures(state, translate_disclosures) {
      state.translate_disclosures = translate_disclosures;
    },
    update_disclosures(state, disclosures) {
      // order_index 기반으로 정렬하여 순서 보장
      if (Array.isArray(disclosures)) {
        // 원본 배열을 변경하지 않도록 복사 후 정렬
        state.disclosures = [...disclosures].sort((a, b) => {
          // order_index가 없으면 맨 뒤로
          const aOrder = a.order_index ?? Number.MAX_VALUE;
          const bOrder = b.order_index ?? Number.MAX_VALUE;

          // order_index가 같으면 id로 정렬 (안정적인 정렬)
          if (aOrder === bOrder) {
            return (a.id || 0) - (b.id || 0);
          }

          return aOrder - bOrder;
        });
      } else {
        state.disclosures = disclosures;
      }
    },
    update_disclosures_loaded(state, flag) {
      state.disclosures_loaded = flag;
    },
    update_page_accessibility(state, flag) {
      state.page_accessibility = flag;
    },
    add_disclosures(state, payload) {
      const { disclosures } = payload;
      const newOrderIndex = disclosures.order_index;
      // 백엔드 clean_order()와 동기화: 삽입 위치 이후 기존 항목 order_index += 1
      state.disclosures.forEach(item => {
        if (item.order_index >= newOrderIndex) {
          item.order_index += 1;
        }
      });
      // 밀어낸 후 newOrderIndex를 가진 항목은 없으므로 > 조건으로 삽입 위치 탐색
      const insertPos = state.disclosures.findIndex(item => item.order_index > newOrderIndex);
      state.disclosures.splice(
        insertPos === -1 ? state.disclosures.length : insertPos,
        0,
        disclosures
      );
    },
    delete_disclosures(state, id) {
      state.disclosures = state.disclosures.filter(item => item.id !== id);
    },
    update_print_mode(state, flag) {
      state.print_mode = flag;
    }
  },
  getters: {
    is_viewer(state, getters, rootState, rootGetters) {
      // 중간에 따로 뽑아 캐싱
      return rootGetters.is_viewer;
    },
    sb_toc(state, getters) {
      if (getters.is_viewer) {
        return state.viewer_sb_toc;
      } else {
        return state.editor_sb_toc;
      }
    },
    selected_viewer_disclosures(state, getters) {
      if (
        getters.selected_lv1 &&
        getters.selected_lv2 &&
        state.viewer_disclosure_map?.[getters.selected_lv1?.key]?.[getters.selected_lv2?.key]
      ) {
        return state.viewer_disclosure_map?.[getters.selected_lv1?.key]?.[
          getters.selected_lv2?.key
        ];
      } else {
        return null;
      }
    },
    selected_lv1(state, getters) {
      const result = getters.is_viewer ? state.viewer_selected_lv1 : state.editor_selected_lv1;
      return result || null;
    },
    selected_lv2(state, getters) {
      const result = getters.is_viewer ? state.viewer_selected_lv2 : state.editor_selected_lv2;
      return result || null;
    },
    ai_main_page_url(state) {
      return state.main_setting?.main_page_template_info?.url;
    },
    banned_topic_keys(state, getters, rootState, rootGetters) {
      const report = rootGetters['report_list/selected_report'];
      if (!report) return {};

      // 보고서 owner는 ban 대상이 아님
      if (String(report.owner_id) === String(rootState.user_id)) return {};

      const email = rootState.e_mail;
      if (!email) return {};

      const coauthors = report.coauthors;
      if (!Array.isArray(coauthors)) return {};

      const me = coauthors.find(c => c.e_mail === email);
      if (!me || !Array.isArray(me.banned_info_list)) return {};

      const keys = {};
      me.banned_info_list.forEach(b => {
        keys[`${b.lv1_key}|${b.lv2_key}`] = true;
      });
      return keys;
    },
    editing_presences(state) {
      return state.editing_presences;
    }
  },
  actions: {
    async fetch_editing_presences({ commit }, { report_code }) {
      try {
        const response = await authApi.get('/presence/' + report_code);
        if (response.data.code) {
          commit('set_editing_presences', response.data.editors || {});
        } else {
          console.warn('[fetch_editing_presences]', response.data.msg);
          commit('set_editing_presences', {});
        }
      } catch (error) {
        console.warn('[fetch_editing_presences]', error.message);
      }
    },

    async send_presence_heartbeat(_, { report_code, sb_toc_lv2_id }) {
      try {
        await authApi.post('/presence/heartbeat', { report_code, sb_toc_lv2_id });
      } catch (error) {
        console.warn('[send_presence_heartbeat]', error.message);
      }
    },

    async leave_editing_presence(_, { report_code, sb_toc_lv2_id }) {
      try {
        const data = { report_code };
        if (sb_toc_lv2_id) data.sb_toc_lv2_id = sb_toc_lv2_id;
        await authApi.delete('/presence/leave', { data });
      } catch (error) {
        console.warn('[leave_editing_presence]', error.message);
      }
    },

    async fetch_main_setting_from_viewer({ commit, rootState }, { report_code }) {
      try {
        const response = await publicApi.get('/storyboard/main_info/' + report_code);

        if (response.data.code) {
          commit('update_main_setting', response.data.main_setting_info);
        }
      } catch (error) {
        error.message = '[웹보고서 기본설정]' + error.message;
        Sentry.captureException(error);
      }
    },
    async initiate_main_setting({ commit, rootState }, { report_code }) {
      try {
        const response = await authApi.put('/storyboard_main/init_main_info/' + report_code);

        if (response.data.code) {
          commit('update_main_setting', response.data.main_setting_info);
        }
      } catch (error) {
        error.message = '[웹보고서 기본설정]' + error.message;
        Sentry.captureException(error);
      }
    },
    async fetch_sb_toc(
      { commit, rootState, getters, rootGetters },
      { report_code, select_lv = true, is_simple = true }
    ) {
      const url = new URL(window.location.href);
      commit('update_toc_loaded', false);
      let view_mode = 'viewer';
      // URLSearchParams 객체를 사용하여 쿼리 파라미터를 가져옵니다.
      const queryParams = new URLSearchParams(url.search);

      try {
        const response = await publicApi.get(
          '/storyboard/toc/' + report_code + '?is_simple=' + is_simple
        );
        if (response.data.code) {
          if (rootGetters.is_viewer) {
            commit('update_viewer_sb_toc', response.data.result);
          } else {
            commit('update_sb_toc', response.data.result);
            view_mode = 'editor';
          }

          commit('update_toc_loaded', true);

          if (response.data.result.length > 0) {
            if (select_lv) {
              commit('update_selected_lv1', {
                lv1_info: response.data.result[0],
                view_mode: view_mode
              });
            }
            // select_lv: false 시에는 기존 selected_lv1을 유지한다.
            // 호출자가 fetch 전후로 직접 lv1을 set하므로 여기서 null로 초기화하면
            // 첫번째 렌더 후 query 매칭이 일어나 화면 깜빡임이 발생한다 (#745).

            queryParams.forEach((value, key) => {
              if (key === 'lv1') {
                commit('update_selected_lv1', {
                  lv1_info: response.data.result.find(
                    item =>
                      item.name === value.replace(/%20/g, ' ').replace(/%0A/g, '\n') ||
                      item.key === value
                  ),
                  view_mode: view_mode
                });
              }
            });

            if (response.data.result[0].children.length > 0) {
              if (select_lv) {
                commit('update_selected_lv2', {
                  lv2_info: response.data.result[0].children[0],
                  view_mode: view_mode
                });
              }
              // select_lv: false 시에는 기존 selected_lv2를 유지 (위 lv1 설명 참조).
              queryParams.forEach((value, key) => {
                if (
                  key === 'lv2' &&
                  getters.selected_lv1 &&
                  Array.isArray(getters.selected_lv1.children)
                ) {
                  const foundItem = getters.selected_lv1.children.find(
                    item =>
                      item.name === value.replace(/%20/g, ' ').replace(/%0A/g, '\n') ||
                      item.key === value
                  );
                  commit('update_selected_lv2', {
                    lv2_info: foundItem || null,
                    view_mode: view_mode
                  });
                }
              });
            } else {
              commit('update_selected_lv2', { lv2_info: null, view_mode });
            }
          } else {
            commit('update_selected_lv1', { lv1_info: null, view_mode });
            commit('update_selected_lv2', { lv2_info: null, view_mode });
          }
        } else {
          console.warn('[fetch_sb_toc]', response.data.msg);
        }
      } catch (error) {
        error.message = '[웹보고서 목차정보]' + error.message;
        Sentry.captureException(error);
      }
    },

    async fetch_sb_disclosures({ commit, rootState }, { report_code, lv2_id = null }) {
      commit('update_disclosures_loaded', false);
      commit('update_current_ban_member', []);

      this.loading = true;
      try {
        const response = await authApi.get('/storyboard/disclosures/' + report_code + '/' + lv2_id);

        if (response?.data.code === true) {
          commit('update_disclosures', response.data.result);
          commit('update_page_accessibility', true);
          commit('update_current_ban_member', response.data.ban_list);
        } else {
          commit('update_page_accessibility', false);
          console.warn('[fetch_sb_disclosures]', response.data.msg);
        }
      } catch (error) {
        if (error?.response?.data) {
          commit('update_page_accessibility', false);
        }
        console.warn('[fetch_sb_disclosures]', error.message);
        error.message = '[웹보고서 내용 불러오기]' + error.message;
        Sentry.captureException(error);
      }
      commit('update_disclosures_loaded', true);
    },

    async fetch_viewer_sb_disclosures(
      { commit, rootState, getters },
      { report_code, selected_lv_info = null }
    ) {
      const isFullLoad =
        !selected_lv_info || !selected_lv_info?.lv1_info || !selected_lv_info?.lv2_info;
      try {
        let request_url = '/storyboard/viewer_disclosures/' + report_code;
        if (!isFullLoad) {
          request_url += '?lv2=' + selected_lv_info.lv2_info.key;
          commit('update_is_fetching_lv2_disclosure', true);
        } else {
          commit('update_disclosures_loaded', false);
          commit('update_viewer_disclosure_map_loaded', false);
        }
        const response = await publicApi.get(request_url);

        if (response?.data.code === true) {
          if (!isFullLoad) {
            commit('update_is_fetching_lv2_disclosure', false);
          } else {
            commit('update_disclosures_loaded', true);
          }
          commit('update_viewer_disclosure_map', response.data.result);
        } else {
          console.log(response.data.msg);
        }
      } catch (error) {
        error.message = '[웹보고서 뷰어 내용 불러오기]' + error.message;
        Sentry.captureException(error);
      } finally {
        if (!isFullLoad) {
          commit('update_is_fetching_lv2_disclosure', false);
        } else {
          commit('update_disclosures_loaded', true);
          commit('update_viewer_disclosure_map_loaded', true);
        }
      }
    },

    async fetch_sb_translated_disclosures({ commit, rootState }, { lv1_id }) {
      try {
        const response = await publicApi.get('/storyboard/translated_disclosures/' + lv1_id);

        if (response?.data.code === true) {
          commit('update_translate_disclosures', response.data.result);
        } else {
          console.warn('[fetch_sb_translated_disclosures]', response.data.msg);
        }
      } catch (error) {
        error.message = '[웹보고서 번역본 불러오기]' + error.message;
        Sentry.captureException(error);
      }
    },

    async addErtContent({ commit, rootState }, { report_code, lv2_key, id, type }) {
      try {
        const response = await authApi.post('/storyboard/ert_content', {
          report_code: report_code,
          lv2_key: lv2_key,
          content_id: id,
          type: type,
          order_index: rootState.story_board.cursor_num + 1
        });

        if (response?.data.code === true) {
          if (response.data.result.type === 'data_table') {
            commit(
              'data_tables/update_linked_story_board',
              {
                table_id: response.data.result.data.id,
                linked_data: response.data.linked_info
              },
              { root: true }
            );
          } else if (response.data.result.type === 'business_case') {
            commit(
              'business_cases/update_linked_story_board',
              {
                business_case_id: response.data.result.data.id,
                linked_data: response.data.linked_info
              },
              { root: true }
            );
          } else if (response.data.result.type === 'document') {
            commit(
              'documents/update_linked_story_board',
              {
                document_id: response.data.result.data.id,
                linked_data: response.data.linked_info
              },
              { root: true }
            );
          } else if (response.data.result.type === 'document-set') {
            commit(
              'documents/update_linked_story_board',
              {
                document_ids: response.data.result.data.id,
                linked_data: response.data.linked_info
              },
              { root: true }
            );
          }
          commit('add_disclosures', {
            disclosures: response.data.result,
            order_index: rootState.story_board.cursor_num + 1
          });
        } else {
          console.warn('[addErtContent]', response.data.msg);
        }
      } catch (error) {
        error.message = '[웹보고서 문서함 추가]' + error.message;
        Sentry.captureException(error);
      }
    },

    async updateErtContent(
      { commit, rootState },
      { id, type, disclosure_id, background_color, guide_mode, alignment, show_title }
    ) {
      try {
        // 필수 필드만 기본 페이로드에 포함
        const payload = {
          content_id: id,
          type: type
        };

        // 선택적 필드: 실제로 전달된 값만 추가
        if (background_color !== undefined) payload.background_color = background_color;
        if (guide_mode !== undefined) payload.guide_mode = guide_mode;
        if (alignment !== undefined) payload.alignment = alignment;
        if (show_title !== undefined) payload.show_title = show_title;

        const response = await authApi.put('/storyboard/ert_content/' + disclosure_id, payload);

        if (response?.data.code === true) {
          commit('update_disclosure', response.data.result);
          if (response.data.result.type === 'data_table') {
            commit(
              'data_tables/update_linked_story_board',
              {
                table_id: response.data.result.data.id,
                linked_data: response.data.linked_info,
                before_item_id: response.data.before_item_id
              },
              { root: true }
            );
          } else if (response.data.result.type === 'business_case') {
            commit(
              'business_cases/update_linked_story_board',
              {
                business_case_id: response.data.result.data.id,
                linked_data: response.data.linked_info,
                before_item_id: response.data.before_item_id
              },
              { root: true }
            );
          } else if (response.data.result.type === 'document') {
            commit(
              'documents/update_linked_story_board',
              {
                document_id: response.data.result.data.id,
                linked_data: response.data.linked_info,
                before_item_id: response.data.before_item_id
              },
              { root: true }
            );
          }
        } else {
          console.warn('[updateErtContent]', response.data.msg);
        }
      } catch (error) {
        error.message = '[웹보고서 문서함 수정]' + error.message;
        Sentry.captureException(error);
      }
      commit('update_disclosures_loaded', true);
    },

    // #777 비즈니스 케이스 양식 토글: save_business_case 전체 페이로드에 bare_mode를 실어 전송한다.
    // 백엔드 모델에서 bare_mode는 GRBusinessCases 컬럼이므로, 토글이라도 다른 필드(title/description/
    // files_id/images_id/badge_text)를 전부 함께 보내야 한다.
    async updateBcBareMode(
      { state, rootState, rootGetters, dispatch },
      { disclosure_id, bare_mode, e_mail }
    ) {
      const target = (state.disclosures || []).find(item => item.id === disclosure_id);
      if (!target || target.type !== 'business_case' || !target.data) return false;

      const business_case_id = target.data.id;
      const bc = (rootState.business_cases.business_case_list || []).find(
        item => item.business_case_id === business_case_id
      );

      // store 동기화가 아직이라면 disclosure.data를 fallback으로 사용
      const source = bc || target.data;

      const result = await dispatch(
        'business_cases/saveBusinessCase',
        {
          report_code: source.report_code || rootGetters['report_list/current_report_code'],
          business_case_id,
          title: source.title || '',
          description: source.description || '',
          files: source.files || [],
          images: source.images || [],
          e_mail,
          badge_text: source.badge_text || '',
          bare_mode: !!bare_mode,
          silent: true
        },
        { root: true }
      );

      return !!(result && result.business_case_id);
    },

    async normalizeDisclosuresOrder({ commit, state, getters }) {
      try {
        const lv2_id = getters.selected_lv2?.key;
        if (!lv2_id || !state.disclosures.length) return;

        const response = await authApi.put('/storyboard/disclosures/order', {
          disclosures: state.disclosures,
          lv2_id
        });
        if (response?.data.code) {
          commit('update_disclosures', response.data.result);
        }
      } catch (error) {
        error.message = '[순서 정규화]' + error.message;
        Sentry.captureException(error);
      }
    },

    async overwriteDisclosures(
      { dispatch, rootState },
      { report_code, selected_lv2_key, sb_log_id }
    ) {
      try {
        const response = await authApi.put('/logging/disclosures', {
          sb_log_id: sb_log_id,
          selected_lv2_key: selected_lv2_key,
          report_code: report_code
        });

        // 덮어쓰면서 새로 생성된 ert contents들을 한번 다시 가져오기
        dispatch('data_tables/readDataTableList', { report_code: report_code }, { root: true });
        dispatch('documents/readDocumentList', { report_code: report_code }, { root: true });
        dispatch(
          'business_cases/readBusinessCaseList',
          { report_code: report_code },
          { root: true }
        );

        return response.data;
      } catch (error) {
        error.message = '[웹보고서 덮어쓰기]' + error.message;
        Sentry.captureException(error);
        throw error;
      }
    }
  }
};

export default story_board;
